import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Chip,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  Alert
} from '@mui/material';
import {
  Send as SendIcon,
  Psychology as PsychologyIcon,
  Person as PersonIcon,
  MoreVert as MoreVertIcon,
  NoteAdd as NoteAddIcon,
  ContentPaste as PasteIcon,
  VolumeUp as VolumeUpIcon
} from '@mui/icons-material';
import * as supervisorApi from '../../services/supervisorApi';
import api from '../../services/api';
import TextToSpeech from '../TextToSpeech';
import SpeechToText from '../SpeechToText';
import ConversationAutoplay from '../therapist/ConversationAutoplay';
import { getPlatformSettings } from '../../services/api';
import { getVoiceForMessage } from '../../utils/voiceUtils';

const SupervisorChatInterface = forwardRef(({ consultation, disabled, onCreateCareNote, token, userSettings }, ref) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState('');
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedMessageIndex, setSelectedMessageIndex] = useState(null);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [streamingSupported, setStreamingSupported] = useState(null);
  const [showAutoplay, setShowAutoplay] = useState(false);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Load initial messages
  useEffect(() => {
    if (consultation?.messages) {
      setMessages(consultation.messages);
    }
  }, [consultation]);

  // Load platform settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getPlatformSettings(token);
        setPlatformSettings(settings);
      } catch (error) {
        console.error('Failed to load platform settings:', error);
      }
    };

    if (token) {
      fetchSettings();
    }
  }, [token]);

  // Detect TTS provider to determine streaming support
  useEffect(() => {
    const detectProvider = async () => {
      if (!token) return;

      try {
        const response = await api.get('/tts/provider-info', token, {
          params: { interface: 'client_chat' }
        });
        const provider = response?.data?.provider?.toLowerCase() || 'elevenlabs';
        const supportsStreaming = provider === 'elevenlabs';
        setStreamingSupported(supportsStreaming);
      } catch (error) {
        console.error('Failed to detect TTS provider:', error);
        setStreamingSupported(false);
      }
    };

    detectProvider();
  }, [token]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    addMessages: (newMessages) => {
      setMessages(prev => [...prev, ...newMessages]);
    },
    setTypingIndicator: (typing) => {
      setIsTyping(typing);
    },
    insertContext: (contextData) => {
      const contextText = formatContextForInsertion(contextData);
      setInputMessage(prev => prev + '\n\n' + contextText);
      inputRef.current?.focus();
    },
    setInitialMessage: (message) => {
      setInputMessage(message);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    },
    sendMessage: (message) => {
      setInputMessage(message);
      // Use setTimeout to ensure state update completes before sending
      setTimeout(() => {
        handleSendMessage();
      }, 0);
    }
  }));

  // Format context data for insertion into message
  const formatContextForInsertion = (contextData) => {
    let text = '---Context---\n';
    
    if (contextData.type === 'assessment') {
      text += `Assessment: ${contextData.data.testKey}\n`;
      text += `Score: ${contextData.data.score}\n`;
      text += `Interpretation: ${contextData.data.interpretation}\n`;
      text += `Date: ${new Date(contextData.data.completedAt).toLocaleDateString()}`;
    } else if (contextData.type === 'goal') {
      text += `Goal: ${contextData.data.title}\n`;
      text += `Progress: ${contextData.data.progress}%\n`;
      text += `Status: ${contextData.data.status}`;
    } else if (contextData.type === 'checkin') {
      text += `Check-in Date: ${new Date(contextData.data.date).toLocaleDateString()}\n`;
      text += `Mood: ${contextData.data.mood}/10\n`;
      if (contextData.data.anxietyLevel) {
        text += `Anxiety: ${contextData.data.anxietyLevel}/10\n`;
      }
      if (contextData.data.notes) {
        text += `Notes: ${contextData.data.notes}`;
      }
    } else if (contextData.type === 'journal') {
      text += `Journal Entry: ${new Date(contextData.data.createdAt).toLocaleDateString()}\n`;
      text += `Mood: ${contextData.data.mood || 'Not specified'}\n`;
      text += `Content: ${contextData.data.content.substring(0, 200)}...`;
    } else if (contextData.type === 'message') {
      text += `Message from ${contextData.data.senderRole} at ${new Date(contextData.data.timestamp).toLocaleString()}\n`;
      text += `"${contextData.data.text}"`;
    }
    
    text += '\n---End Context---';
    return text;
  };

  // Send message
  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isSending || disabled) return;

    const messageText = inputMessage.trim();
    setInputMessage('');
    setIsSending(true);
    setError('');

    // Add therapist message optimistically
    const tempTherapistMessage = {
      senderRole: 'therapist',
      text: messageText,
      timestamp: new Date(),
      _id: 'temp-' + Date.now()
    };
    setMessages(prev => [...prev, tempTherapistMessage]);

    try {
      const response = await supervisorApi.sendMessage(consultation._id, messageText, token);
      
      // Replace temp message with real ones
      setMessages(prev => {
        const filtered = prev.filter(msg => msg._id !== tempTherapistMessage._id);
        return [...filtered, response.therapistMessage, response.supervisorMessage];
      });
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');
      // Remove temp message on error
      setMessages(prev => prev.filter(msg => msg._id !== tempTherapistMessage._id));
      setInputMessage(messageText); // Restore input
    } finally {
      setIsSending(false);
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Context menu handlers
  const handleContextMenu = (event, index) => {
    event.preventDefault();
    setSelectedMessageIndex(index);
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setSelectedMessageIndex(null);
  };

  const handleCopyMessage = () => {
    if (selectedMessageIndex !== null) {
      navigator.clipboard.writeText(messages[selectedMessageIndex].text);
    }
    handleCloseContextMenu();
  };

  const handleCreateCareNoteFromMessage = () => {
    if (selectedMessageIndex !== null && onCreateCareNote) {
      const message = messages[selectedMessageIndex];
      onCreateCareNote(message.text);
    }
    handleCloseContextMenu();
  };

  // Render message
  const renderMessage = (message, index) => {
    const isTherapist = message.senderRole === 'therapist';
    const isSupervisor = message.senderRole === 'supervisor';
    const isHighlighted = highlightedMessageId === message._id;

    const voiceId = getVoiceForMessage(message, userSettings, platformSettings);

    return (
      <Box
        key={message._id || index}
        data-message-id={message._id}
        sx={{
          display: 'flex',
          justifyContent: isTherapist ? 'flex-end' : 'flex-start',
          mb: 2,
          px: 2
        }}
        onContextMenu={(e) => handleContextMenu(e, index)}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            maxWidth: '70%',
            flexDirection: isTherapist ? 'row-reverse' : 'row'
          }}
        >
          <Avatar
            sx={{
              bgcolor: isTherapist ? 'primary.main' : 'secondary.main',
              mx: 1
            }}
          >
            {isTherapist ? <PersonIcon /> : <PsychologyIcon />}
          </Avatar>
          
          <Paper
            elevation={1}
            sx={{
              p: 2,
              bgcolor: isTherapist ? 'primary.light' : 'grey.100',
              color: isTherapist ? 'primary.contrastText' : 'text.primary',
              borderRadius: 2,
              position: 'relative',
              ...(isHighlighted && {
                animation: 'pulse 2s ease-in-out',
                boxShadow: '0 0 20px rgba(255,140,0,0.3)',
                '@keyframes pulse': {
                  '0%, 100%': {
                    boxShadow: '0 0 20px rgba(255,140,0,0.3)',
                  },
                  '50%': {
                    boxShadow: '0 0 30px rgba(255,140,0,0.5)',
                  },
                },
              }),
            }}
          >
            <Box display="flex" alignItems="center" mb={0.5}>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                {isTherapist ? 'You' : 'AI Supervisor'}
              </Typography>
              <Typography variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
                {new Date(message.timestamp).toLocaleTimeString()}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  flex: 1
                }}
              >
                {message.text}
              </Typography>
              <TextToSpeech
                text={message.text}
                voiceId={voiceId}
                platformSettings={platformSettings}
                userSettings={userSettings}
                size="small"
                useStreaming={streamingSupported === true}
                senderRole={message.senderRole}
                useBackendTTS={false}
                token={token}
              />
            </Box>

          </Paper>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Messages Area */}
      <Box
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          py: 2,
          bgcolor: 'background.default'
        }}
      >
        {messages.length === 0 && (
          <Box sx={{ textAlign: 'center', mt: 4, px: 3 }}>
            <PsychologyIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Start Your Supervisor Consultation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              A suggested message has been pre-filled below. Feel free to edit it before sending.
              Your first message will automatically include all relevant client context.
            </Typography>
          </Box>
        )}

        {messages.map((message, index) => renderMessage(message, index))}
        
        {isTyping && (
          <Box sx={{ display: 'flex', alignItems: 'center', px: 3, mb: 2 }}>
            <Avatar sx={{ bgcolor: 'secondary.main', mr: 1 }}>
              <PsychologyIcon />
            </Avatar>
            <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Supervisor is thinking...
                </Typography>
              </Box>
            </Paper>
          </Box>
        )}
        
        <div ref={messagesEndRef} />
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mx: 2 }}>
          {error}
        </Alert>
      )}

      {/* Input Area */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 2, 
          borderTop: 1, 
          borderColor: 'divider',
          bgcolor: disabled ? 'grey.100' : 'background.paper'
        }}
      >
        <Box display="flex" alignItems="flex-end" gap={1}>
          <TextField
            ref={inputRef}
            fullWidth
            multiline
            maxRows={4}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={disabled ? "Consultation completed" : "Type your message..."}
            disabled={disabled || isSending}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3
              }
            }}
          />
          
          <SpeechToText
            onTranscript={(transcript) => {
              // Append transcript to existing text
              const newValue = inputMessage ? `${inputMessage} ${transcript}` : transcript;
              setInputMessage(newValue);
              inputRef.current?.focus();
            }}
            disabled={disabled || isSending}
            continuous={false}
            showInterimText={false}
          />

          <Tooltip title="AutoPlay conversation">
            <IconButton
              onClick={() => setShowAutoplay(!showAutoplay)}
              disabled={messages.length === 0}
              color={showAutoplay ? "primary" : "default"}
              sx={{
                ...(showAutoplay && {
                  bgcolor: 'primary.light'
                })
              }}
            >
              <VolumeUpIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Create care note">
            <IconButton
              onClick={onCreateCareNote}
              disabled={disabled}
              color="primary"
            >
              <NoteAddIcon />
            </IconButton>
          </Tooltip>
          
          <IconButton
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isSending || disabled}
            color="primary"
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark'
              },
              '&:disabled': {
                bgcolor: 'grey.300'
              }
            }}
          >
            {isSending ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
          </IconButton>
        </Box>
        
        {!disabled && messages.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Your first message will include full client context automatically. You can also add specific data using the context browser.
          </Typography>
        )}
        {!disabled && messages.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Tip: Use the context browser on the left to reference specific client data
          </Typography>
        )}
      </Paper>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleCopyMessage}>
          <PasteIcon fontSize="small" sx={{ mr: 1 }} />
          Copy Message
        </MenuItem>
        {selectedMessageIndex !== null && messages[selectedMessageIndex]?.senderRole === 'supervisor' && (
          <MenuItem onClick={handleCreateCareNoteFromMessage}>
            <NoteAddIcon fontSize="small" sx={{ mr: 1 }} />
            Create Care Note from This
          </MenuItem>
        )}
      </Menu>

      {/* AutoPlay Control Panel */}
      {showAutoplay && (
        <ConversationAutoplay
          messages={messages}
          userSettings={userSettings}
          platformSettings={platformSettings}
          therapistSettings={null}
          clientId={null}
          conversationId={null}
          token={token}
          onMessageHighlight={(messageId) => {
            setHighlightedMessageId(messageId);
            const messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
            if (messageElement) {
              messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }}
          onClose={() => setShowAutoplay(false)}
        />
      )}
    </Box>
  );
});

SupervisorChatInterface.displayName = 'SupervisorChatInterface';

export default SupervisorChatInterface;